import { zodResolver } from '@hookform/resolvers/zod';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  createArticle,
  getArticle,
  listCategories,
  listTauxTva,
  listUnites,
  updateArticle,
} from '@/modules/catalogue/api';

const schema = z.object({
  reference: z.string(),
  kind: z.enum(['materiau', 'prestation', 'main_oeuvre', 'forfait', 'sous_traitance']),
  libelle: z.string().min(1, 'Désignation obligatoire'),
  description: z.string(),
  categorie_id: z.string(),
  unite_id: z.string().min(1, 'Unité obligatoire'),
  taux_tva_id: z.string().min(1, 'Taux de TVA obligatoire'),
  prix_vente_ht: z.string(),
  actif: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  reference: '',
  kind: 'materiau',
  libelle: '',
  description: '',
  categorie_id: '',
  unite_id: '',
  taux_tva_id: '',
  prix_vente_ht: '',
  actif: true,
};

function normalizePrice(value: string): string {
  const v = value.trim().replace(',', '.');
  return v === '' ? '0' : v;
}

export function ArticleFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const unitesQ = useQuery({ queryKey: ['unites'], queryFn: listUnites });
  const tvaQ = useQuery({ queryKey: ['taux_tva'], queryFn: listTauxTva });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: listCategories });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const editQuery = useQuery({
    queryKey: ['article', id],
    queryFn: () => getArticle(id as string),
    enabled: isEdit,
  });

  useEffect(() => {
    const a = editQuery.data;
    if (!a) return;
    reset({
      reference: a.reference ?? '',
      kind: a.kind as FormValues['kind'],
      libelle: a.libelle,
      description: a.description ?? '',
      categorie_id: a.categorie_id ?? '',
      unite_id: a.unite_id,
      taux_tva_id: a.taux_tva_id,
      prix_vente_ht: a.prix_vente_ht,
      actif: a.actif,
    });
  }, [editQuery.data, reset]);

  const mutation = useMutation({
    mutationFn: async (v: FormValues) => {
      const input = {
        ...v,
        reference: v.reference.trim() === '' ? null : v.reference.trim(),
        categorie_id: v.categorie_id === '' ? null : v.categorie_id,
        prix_vente_ht: normalizePrice(v.prix_vente_ht),
      };
      if (isEdit && id) await updateArticle(id, input);
      else await createArticle(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
      navigate('/prestations');
    },
  });

  const onSubmit = handleSubmit((v) => {
    mutation.mutate(v);
  });

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/prestations" aria-label="Retour">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          {isEdit ? "Modifier l'article" : 'Nouvel article'}
        </h1>
      </div>

      {mutation.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Échec : {messageErreur(mutation.error)}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Désignation</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="libelle">Désignation *</Label>
            <Input id="libelle" {...register('libelle')} />
            {errors.libelle ? (
              <p className="text-xs text-destructive">{errors.libelle.message}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reference">Référence</Label>
            <Input id="reference" {...register('reference')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kind">Type</Label>
            <Select id="kind" {...register('kind')}>
              <option value="materiau">Matériau</option>
              <option value="prestation">Prestation</option>
              <option value="main_oeuvre">Main d'œuvre</option>
              <option value="forfait">Forfait</option>
              <option value="sous_traitance">Sous-traitance</option>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="categorie_id">Catégorie</Label>
            <Select id="categorie_id" {...register('categorie_id')}>
              <option value="">— Aucune —</option>
              {(catQ.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" {...register('description')} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tarification &amp; unité</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="unite_id">Unité *</Label>
            <Select id="unite_id" {...register('unite_id')}>
              <option value="">— Choisir —</option>
              {(unitesQ.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.libelle}
                </option>
              ))}
            </Select>
            {errors.unite_id ? (
              <p className="text-xs text-destructive">{errors.unite_id.message}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="taux_tva_id">Taux de TVA *</Label>
            <Select id="taux_tva_id" {...register('taux_tva_id')}>
              <option value="">— Choisir —</option>
              {(tvaQ.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.libelle}
                </option>
              ))}
            </Select>
            {errors.taux_tva_id ? (
              <p className="text-xs text-destructive">{errors.taux_tva_id.message}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="prix_vente_ht">Prix de vente HT (€)</Label>
            <Input id="prix_vente_ht" inputMode="decimal" placeholder="0.00" {...register('prix_vente_ht')} />
          </div>
          <div className="flex items-center gap-2 sm:col-span-2">
            <input
              id="actif"
              type="checkbox"
              className="h-4 w-4 rounded border-input accent-primary"
              {...register('actif')}
            />
            <Label htmlFor="actif">Article actif (proposable dans les devis)</Label>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : "Créer l'article"}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/prestations">Annuler</Link>
        </Button>
      </div>
    </form>
  );
}
